import {
  ComponentMultiStyleConfig,
  Text,
  useMultiStyleConfig,
  VStack,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { ComponentStyleProps } from "../../../../../lib/styles/props/component-style-props";
import { CMFormCommentText } from "../../text";
import { hasValue } from "../../../../../lib/util/common-util";
import { CMFormCommentInput } from "../../control";
import { DeleteConfirmationModal } from "../../../../modal";
import { CMDummyTextareaButton } from "../../../../_components";

export type CMItemEditRequestProps = {
  /**
   * 編集ボタンのラベル・Ariaラベル。
   * 編集可能な場合、設定を強く推奨する。
   */
  editButtonLabel?: string;
  /**
   * テキストエリアの最大文字数。
   * 指定しなければ無制限。
   */
  maxLength?: number;

  commentInput: {
    placeholder?: string;
    finishButtonLabel: string;
    cancelButtonLabel: string;
    deleteButtonLabel: string;
  };

  deleteConfirmationModal: {
    title: string;
    message?: string;
    deleteButtonLabel: string;
    cancelButtonLabel: string;
  };

  /**
   * 削除確認モーダルの制御に使用する。
   */
  deleteConfirmationModalSettings: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
  };

  content?: {
    userName: string;
    date: Date;
    comment: string;
  };

  /**
   * 編集開始または編集することが可能である。
   */
  editable: boolean;
  /**
   * 保存して編集終了したときのコールバック。
   * nullに更新する場合、削除を表す。
   */
  onSave?: (value: string | null) => void;
} & ComponentStyleProps;

export const CMItemEditRequest = ({
  editButtonLabel,
  maxLength,

  commentInput,

  deleteConfirmationModal,
  deleteConfirmationModalSettings,

  content,

  editable,
  onSave,

  sx,
  ...rest
}: CMItemEditRequestProps) => {
  const styles = useMultiStyleConfig("CMItemEditRequestStyles");

  const [editing, setEditing] = useState(false);

  // 編集中の場合
  if (editing) {
    return (
      <>
        <CMFormCommentInput
          placeholder={commentInput.placeholder}
          finishButtonLabel={commentInput.finishButtonLabel}
          cancelButtonLabel={commentInput.cancelButtonLabel}
          deleteButtonLabel={commentInput.deleteButtonLabel}
          focusOnMount={true}
          maxLength={maxLength}
          value={content?.comment ?? ""}
          onSave={(value) => {
            setEditing(false);
            if (hasValue(onSave)) onSave(value);
          }}
          onCancel={() => setEditing(false)}
          onDelete={deleteConfirmationModalSettings.onOpen}
          sx={{ ...styles.container, ...sx }}
          {...rest}
        />
        {/*削除確認モーダル*/}
        <DeleteConfirmationModal
          isOpen={deleteConfirmationModalSettings.isOpen}
          onClose={deleteConfirmationModalSettings.onClose}
          title={deleteConfirmationModal.title}
          message={deleteConfirmationModal.message}
          deleteButtonLabel={deleteConfirmationModal.deleteButtonLabel}
          cancelButtonLabel={deleteConfirmationModal.cancelButtonLabel}
          onConfirm={() => {
            setEditing(false);
            onSave?.(null);
          }}
        />
      </>
    );
  }

  return hasValue(content) ? (
    // 表示項目あり
    <CMFormCommentText
      userName={content.userName}
      date={content.date}
      comment={content.comment}
      editable={editable}
      onEdit={() => setEditing(true)}
      editButtonLabel={editButtonLabel}
      sx={{ ...styles.container, ...sx }}
      {...rest}
    />
  ) : editable ? (
    // 編集可能
    <VStack sx={{ ...styles.container, ...sx }} {...rest}>
      <CMDummyTextareaButton
        label={commentInput.placeholder}
        onClick={() => setEditing(true)}
        sx={styles.dummyTextareaButton}
      />
    </VStack>
  ) : (
    // 編集不可・表示項目なし
    <VStack sx={{ ...styles.container, ...sx }} {...rest}>
      <Text>No content</Text>
    </VStack>
  );
};

export const CMItemEditRequestStyles: ComponentMultiStyleConfig = {
  parts: ["container", "dummyTextareaButton"],
  baseStyle: {
    container: {},
    dummyTextareaButton: {
      mt: "1",
    },
  },
};
